import { ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonState = 'default' | 'loading';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: ButtonState;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
}

export const Button = ({
  variant = 'primary',
  size = 'md',
  state = 'default',
  children,
  onClick,
  disabled = false,
  className = '',
  type = 'button',
  fullWidth = false
}: ButtonProps) => {
  const baseClasses = 'rounded-xl font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-950 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97]';
  const widthClass = fullWidth ? 'w-full' : '';

  const variantClasses = {
    primary: 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 hover:-translate-y-0.5 focus:ring-cyan-500 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40',
    secondary: 'bg-white/[0.04] backdrop-blur-sm text-slate-200 border border-white/10 hover:bg-white/[0.08] hover:border-white/15 focus:ring-slate-400 hover:-translate-y-0.5',
    danger: 'bg-gradient-to-r from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 hover:-translate-y-0.5 focus:ring-red-500 shadow-lg shadow-red-500/25 hover:shadow-red-500/40',
    ghost: 'text-slate-400 hover:text-white hover:bg-white/[0.05] focus:ring-slate-400',
    success: 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 hover:-translate-y-0.5 focus:ring-emerald-500 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40'
  };

  const sizeClasses = {
    sm: 'px-3.5 py-2 text-sm gap-1.5',
    md: 'px-5 py-3 text-base gap-2',
    lg: 'px-7 py-3.5 text-lg gap-2.5'
  };

  const isLoading = state === 'loading';
  const isDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} flex items-center justify-center ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      onClick={onClick}
      disabled={isDisabled}
    >
      {isLoading && (
        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  );
};